import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import ReferralSelect from '@/common/ReferralSelect'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildRemoveReferralMessage,
  RemoveReferralFormValues,
} from './referralMessageBuilder'

const removeReferralSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  id: z
    .string()
    .min(1, 'Referral ID is required')
    .refine(
      value =>
        !isNaN(Number(value)) &&
        Number.isInteger(Number(value)) &&
        Number(value) >= 0 &&
        Number(value) <= 65535,
      {
        message: 'ID must be a valid 16-bit unsigned integer (0-65535)',
      },
    ),
})

export default function RemoveReferralForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<RemoveReferralFormValues>({
    resolver: zodResolver(removeReferralSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      id: '',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildRemoveReferralMessage(
        watchedValues as RemoveReferralFormValues,
      )
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Remove Referral Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to remove a referral by ID
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>
            Referral Identification
          </h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel>Select Referral</UiLabel>
              <ReferralSelect
                control={form.control}
                name='id'
                onValueChange={referralId =>
                  form.setValue('id', referralId, {
                    shouldValidate: true,
                  })
                }
                disabled={isSubmitting}
                placeholder='Select a referral to remove'
              />
              {form.formState.errors.id && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.id.message}
                </p>
              )}
            </div>
            {form.formState.errors.id && (
              <p className='text-destructive text-sm'>
                {form.formState.errors.id.message}
              </p>
            )}
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'remove-referral-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildRemoveReferralMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
